// Pizza factory levels. The id is fixed (progress is saved under it) – don't change it once the level is published.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { LevelData, PlacementData, StaticData, GoalData } from '../../core/types';
import { RS, RB, at, meet, meetFar, onBelt, type Pt } from './shared';

const floorBox = (x: number, w?: number): StaticData => ({ x, y: 690, w: w || 260, h: 30, skin: 'pizza_steel' });
const box = (x: number, w?: number): GoalData => ({ type: 'bowl', x, y: 666, w: w || 170 });
const wallR: StaticData = { x: 1135, y: 560, w: 30, h: 240, skin: 'pizza_wall' }, wallL: StaticData = { x: 65, y: 560, w: 30, h: 240, skin: 'pizza_wall' };
const G1 = (p: Pt): PlacementData => ({ t: 'pizza_wheel', x: p[0], y: p[1], a: 0 }), G2 = (p: Pt): PlacementData => ({ t: 'pizza_big_wheel', x: p[0], y: p[1], a: 0 });

// 4 + 5: same machine – one wheel (same way as the motor) or via the fixed wheel (the opposite way)
function dirLevel(goalRight: boolean): LevelData {
  const BL: Pt = [480, 430], BR: Pt = [680, 430], M: Pt = [540, 560], F: Pt = [680, 506];
  const A = meetFar(M, 2 * RS, BL, 2 * RS, F), C = meetFar(M, 2 * RS, F, 2 * RS, BR);
  return {
    par: 1, tools: { pizza_wheel: 1 }, learn: ['gears', 'direction'],
    statics: [floorBox(goalRight ? 800 : 360, 220), { x: goalRight ? 935 : 225, y: 590, w: 30, h: 180, skin: 'pizza_wall' }],
    balls: [onBelt(580, 430)],
    goal: box(goalRight ? 800 : 360, 160),
    objs: [
      { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
      { t: 'pizza_gear', x: F[0], y: F[1], s: 's' },
      { t: 'pizza_pegs', p: [A, C] },
      { t: 'pizza_belt', x1: 480, x2: 680, y: 430, drive: 'L', gear: 's', gear2: 's' },
      { t: 'duck', x: goalRight ? 750 : 410, y: 480 },
    ],
    solution: [G1(goalRight ? A : C)],
    fails: [[G1(goalRight ? C : A)]],
  };
}
// 6 + 7: double gear – small wheel on the belt = fast, big wheel = slow
function speedLevel(fast: boolean): LevelData {
  const B: Pt = [440, 470], F = at(B, RB, RS, 120), M = at(F, RB, RS, 35);
  return {
    par: 1, tools: { pizza_wheel: 1, pizza_big_wheel: 1 }, learn: ['gears', 'speed'], intro: fast ? 'pizza_big_wheel' : undefined,
    statics: fast ? [floorBox(1010, 220), wallR, { x: 870, y: 640, w: 60, h: 130, skin: 'pizza_wall' }] : [floorBox(830, 220)],
    balls: [onBelt(560, 470)],
    goal: box(fast ? 1010 : 830, 160),
    objs: [
      { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
      { t: 'pizza_belt', x1: 440, x2: 760, y: 470, drive: 'L', k: 1.5 },
      { t: 'pizza_gearfront', x: F[0], y: F[1], s: 'sb' },
      { t: 'duck', x: fast ? 900 : 700, y: fast ? 420 : 560 },
    ],
    solution: [fast ? G1(B) : G2(B)],
    fails: [[fast ? G2(B) : G1(B)]],
  };
}

export const levels = defineLevels([
  // 1 – The cog – one wheel connects the motor to the belt (new: small gear)
  (() => {
    const A: Pt = [560, 520], P = at(A, RS, RS, 125), M = at(P, RS, RS, 180);
    return {
      id: 'gear', par: 1, tools: { pizza_wheel: 1 }, learn: ['gears', 'mechanism'], intro: 'pizza_wheel',
      statics: [{ x: 1000, y: 690, w: 260, h: 30, skin: 'pizza_steel' }, { x: 1135, y: 560, w: 30, h: 240, skin: 'pizza_wall' }],
      balls: [onBelt(640, 520)],
      goal: { type: 'bowl', x: 1010, y: 666, w: 170 },
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
        { t: 'pizza_pegs', p: [P] },
        { t: 'pizza_belt', x1: 560, x2: 880, y: 520, drive: 'L', gear: 's' },
        { t: 'duck', x: 1000, y: 470 },
      ],
      solution: [{ t: 'pizza_wheel', x: P[0], y: P[1], a: 0 }],
    };
  })(),

  // 2 – The other way – the motor turns anticlockwise → the belt runs left
  (() => {
    const B: Pt = [640, 520], P = at(B, RS, RS, 55), M = at(P, RS, RS, 0);
    return {
      id: 'other-way', par: 1, tools: { pizza_wheel: 1 }, learn: ['gears', 'direction'],
      statics: [floorBox(190), wallL],
      balls: [onBelt(560, 520)],
      goal: box(190),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: -1 },
        { t: 'pizza_pegs', p: [P] },
        { t: 'pizza_belt', x1: 320, x2: 640, y: 520, drive: 'R', gear: 's' },
        { t: 'duck', x: 200, y: 470 },
      ],
      solution: [G1(P)],
    };
  })(),

  // 3 – The dough roller – the roller lifts the tomato over when it turns clockwise (gear intro again)
  (() => {
    const s1: StaticData = { x: 270, y: 380, w: 360, h: 22, a: 20, skin: 'pizza_table' };
    const V: Pt = [482, 432], M = at(V, RS, RS, 95);
    return {
      id: 'dough-roller', par: 1, tools: { pizza_wheel: 1 }, learn: ['gears', 'mechanism'], intro: 'pizza_wheel',
      statics: [s1, floorBox(760), wallR],
      balls: [on(s1, -60, 15)],
      goal: box(760, 190),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: -1 },
        { t: 'pizza_roller', x: V[0], y: V[1], r: 30 },
        { t: 'duck', x: 560, y: 330 },
      ],
      solution: [G1(V)],
    };
  })(),

  // 4 – Right or left – put the wheel where the belt runs right (same way as the motor)
  { id: 'left-or-right', ...dirLevel(true) },

  // 5 – Reversed – same machine; via the fixed wheel the belt runs left
  { id: 'reversed', ...dirLevel(false) },

  // 6 – Fast belt – small wheel on the belt's double gear = fast, over the wall (new: big gear)
  { id: 'fast-belt', ...speedLevel(true) },

  // 7 – Slow belt – big wheel = slow, so the tomato drops into the near box
  { id: 'slow-belt', ...speedLevel(false) },

  // 8 – Two wheels – two wheels turn the direction around; the shortcut (one wheel) runs the wrong way
  (() => {
    const B: Pt = [720, 430], M: Pt = [760, 600];
    const P3 = meet(M, RS + RB, B, RS + RB, 1), P1 = at(M, RS, RS, -45), P2 = meetFar(P1, 2 * RS, B, 2 * RS, M);
    return {
      id: 'two-wheels', par: 2, tools: { pizza_wheel: 2, pizza_big_wheel: 1 }, learn: ['gears', 'direction'],
      statics: [floorBox(290), wallL],
      balls: [onBelt(640, 430)],
      goal: box(290),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
        { t: 'pizza_pegs', p: [P1, P2, P3] },
        { t: 'pizza_belt', x1: 420, x2: 720, y: 430, drive: 'R', gear: 's' },
        { t: 'duck', x: 900, y: 330 },
      ],
      solution: [G1(P1), G1(P2)],
      fails: [[G2(P3)], [G1(P3)], [G2(P3), G1(P1)]],
    };
  })(),

  // 9 – Big jump – only the big wheel makes it all the way over
  (() => {
    const B: Pt = [560, 470], M: Pt = [520, 650], P = meet(M, RS + RB, B, RS + RB, 1);
    return {
      id: 'big-jump', par: 1, tools: { pizza_wheel: 1, pizza_big_wheel: 1 }, learn: ['gears', 'speed'],
      statics: [floorBox(1010, 220), wallR],
      balls: [onBelt(660, 470)],
      goal: box(1010, 170),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
        { t: 'pizza_pegs', p: [P] },
        { t: 'pizza_belt', x1: 560, x2: 860, y: 470, drive: 'L', gear: 's' },
        { t: 'duck', x: 980, y: 420 },
      ],
      solution: [G2(P)],
      fails: [[G1(P)]],
    };
  })(),

  // 10 – The roller turns – the roller must turn the right way (the motor turns clockwise → two wheels)
  (() => {
    const s1: StaticData = { x: 270, y: 380, w: 360, h: 22, a: 20, skin: 'pizza_table' };
    const V: Pt = [482, 432], M: Pt = [500, 600];
    const P = meet(V, 2 * RS, M, 2 * RS, -1), Q1 = at(M, RS, RS, -150), Q2 = meet(Q1, 2 * RS, V, 2 * RS, -1);
    return {
      id: 'roller-turns', par: 2, tools: { pizza_wheel: 2, pizza_big_wheel: 1 }, learn: ['gears', 'direction'],
      statics: [s1, floorBox(780)],
      balls: [on(s1, -150, 15)],
      goal: box(780),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: -1 },
        { t: 'pizza_roller', x: V[0], y: V[1], r: 30, gear: 's' },
        { t: 'pizza_pegs', p: [P, Q1, Q2] },
        { t: 'duck', x: 640, y: 300 },
      ],
      solution: [G1(Q1), G1(Q2)],
      fails: [[G1(P)], [G1(P), G1(Q1)]],
    };
  })(),

  // 11 – Stuck – the triangle locks: three wheels that all touch each other can't turn
  (() => {
    const M: Pt = [520, 600], P1 = at(M, RS, RS, -120), P2 = at(M, RS, RS, -60), B = at(P1, RS, RS, -60);
    return {
      id: 'stuck', par: 1, tools: { pizza_wheel: 2 }, learn: ['gears', 'mechanism'],
      statics: [floorBox(1010, 220), wallR],
      balls: [onBelt(640, B[1])],
      goal: box(1010),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
        { t: 'pizza_pegs', p: [P1, P2] },
        { t: 'pizza_belt', x1: B[0], x2: 860, y: B[1], drive: 'L', gear: 's' },
        { t: 'duck', x: 960, y: 400 },
      ],
      solution: [G1(P1)],
      fails: [[G1(P1), G1(P2)]],
    };
  })(),

  // 12 – The oven – pizza peel + gear get the tomato into the oven
  (() => {
    const s1: StaticData = { x: 180, y: 220, w: 280, h: 22, a: 14, skin: 'pizza_table' };
    const B: Pt = [520, 470], M: Pt = [470, 620], P = meet(M, 2 * RS, B, 2 * RS, 1), Pd = at(M, RS, RS, 180);
    return {
      id: 'oven', par: 2, tools: { plank: 1, pizza_wheel: 2 }, learn: ['gears', 'ramp'],
      statics: [s1, { x: 1030, y: 560, w: 200, h: 40, skin: 'pizza_wall' }],
      balls: [on(s1, -100, 15)],
      goal: { type: 'bell', x: 1030, y: 498 },
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
        { t: 'pizza_pegs', p: [P, Pd] },
        { t: 'pizza_belt', x1: 520, x2: 840, y: 470, drive: 'L', gear: 's' },
        { t: 'duck', x: 420, y: 250 },
      ],
      solution: [{ t: 'plank', x: 400, y: 360, a: 30 }, G1(P)],
      fails: [[G1(P)], [{ t: 'plank', x: 400, y: 360, a: 30 }]],
    };
  })(),

  // 13 – Over the wall – direction + speed (two wheels)
  (() => {
    const B: Pt = [440, 470], F = at(B, RB, RS, 120), P = at(F, RB, RS, 200), M = at(P, RS, RS, 115);
    return {
      id: 'over-the-wall', par: 2, tools: { pizza_wheel: 2, pizza_big_wheel: 1 }, learn: ['gears', 'speed', 'direction'],
      statics: [floorBox(1010, 220), wallR, { x: 870, y: 640, w: 60, h: 130, skin: 'pizza_wall' }],
      balls: [onBelt(560, 470)],
      goal: box(1010, 160),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: -1 },
        { t: 'pizza_pegs', p: [P] },
        { t: 'pizza_belt', x1: 440, x2: 760, y: 470, drive: 'L', k: 1.5 },
        { t: 'pizza_gearfront', x: F[0], y: F[1], s: 'sb' },
        { t: 'duck', x: 640, y: 330 },
      ],
      solution: [G1(P), G1(B)],
      fails: [[G1(P), G2(B)], [G2(P), G1(B)], [G1(B)]],
    };
  })(),

  // 14 – The lump of dough – bounce up onto the belt, which must run the right way
  (() => {
    const s1: StaticData = { x: 990, y: 250, w: 300, h: 22, a: -14, skin: 'pizza_table' };
    const B: Pt = [540, 450], P = at(B, RS, RS, 100), M = at(P, RS, RS, 180), Q = at(M, RS, RS, 60);
    return {
      id: 'dough-lump', par: 2, tools: { pad: 1, pizza_wheel: 2, plank: 1 }, learn: ['gears', 'spring', 'direction'],
      statics: [s1, floorBox(180), wallL, { x: 440, y: 700, w: 240, h: 220, skin: 'pizza_steel' }, { x: 740, y: 735, w: 330, h: 30, skin: 'pizza_steel' }, { x: 920, y: 620, w: 30, h: 260, skin: 'pizza_wall' }],
      balls: [on(s1, 100, 15)],
      goal: box(180),
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: -1 },
        { t: 'pizza_pegs', p: [P, Q] },
        { t: 'pizza_belt', x1: 300, x2: B[0], y: B[1], drive: 'R', gear: 's' },
        { t: 'duck', x: 700, y: 300 },
      ],
      solution: [{ t: 'pad', x: 620, y: 600, a: -15 }, G1(P)],
      fails: [[{ t: 'pad', x: 620, y: 600, a: -15 }], [G1(P)], [{ t: 'pad', x: 620, y: 600, a: -15 }, G1(Q)]],
    };
  })(),

  // 15 – Pizza master – the roller must lift, the belt must run, the peel over the wall – several ways to the goal
  (() => {
    const s1: StaticData = { x: 270, y: 300, w: 360, h: 22, a: 20, skin: 'pizza_table' };
    const V: Pt = [482, 352], M: Pt = [500, 540], B: Pt = [640, 595];
    const Y = meetFar(M, RS + RB, V, RS + RB, [700, 400]);
    const X = meetFar(M, RS + RB, B, RS + RB, [400, 700]);
    const Z = meet(M, 2 * RS, B, 2 * RS, -1);
    const peel: PlacementData = { t: 'plank', x: 940, y: 620, a: 15 };
    return {
      id: 'pizza-master', par: 3, tools: { pizza_wheel: 2, pizza_big_wheel: 2, plank: 1 }, learn: ['gears', 'direction', 'chain_reaction', 'ramp'],
      statics: [s1, { x: 965, y: 708, w: 30, h: 185, skin: 'pizza_wall' }],
      balls: [on(s1, -60, 15)],
      goal: { type: 'bowl', x: 1070, y: 770, w: 150 },
      objs: [
        { t: 'pizza_gear', x: M[0], y: M[1], motor: 1 },
        { t: 'pizza_roller', x: V[0], y: V[1], r: 30, gear: 's', k: 0.6 },
        { t: 'pizza_pegs', p: [Y, X, Z] },
        { t: 'pizza_belt', x1: B[0], x2: 880, y: B[1], drive: 'L', gear: 's' },
        { t: 'duck', x: 760, y: 420 },
      ],
      solution: [G2(Y), G1(Z), peel],
      alt: [G2(Y), G2(X), peel],
      fails: [[G2(Y), G1(Z)], [G1(Z), peel], [G1(Y), G2(X), peel], [G2(Y), peel]],
    };
  })(),
]);
